// Explain why retained history or projects prevent deletion. Only deletable
// teams can submit the typed confirmation; DELETE rechecks the same references.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TeamsRequestError, teamNotEmptyCounts, type Team } from '../../api/teams'
import { useDeleteTeam, useTeamDeletionPreview } from '../../hooks/useTeams'
import { cn } from '../../lib/utils'
import {
  BackButton,
  DestructiveButton,
  ErrorText,
  SecondaryButton,
  WarningPanel,
} from './teamsChrome'
import { resultOf, trackInteraction } from '../../analytics/interactionAnalytics'
import { countBucket } from '../../analytics/buckets'

export default function DeleteTeamView({
  team,
  onDone,
  onCancel,
}: {
  team: Team
  onDone: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation('settings')
  const deleteTeam = useDeleteTeam()
  const preview = useTeamDeletionPreview(team.id)
  const [typedName, setTypedName] = useState('')
  const [blocked, setBlocked] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const previewBlock = !preview.isSuccess || preview.data.canDelete ? null
    : preview.data.reasons.some((reason) => reason === 'credit_history' || reason === 'checkout_orders')
      ? t('settingsModal.teams.deleteBlockedHistory')
      : preview.data.reasons.includes('generation_jobs')
        ? t('settingsModal.teams.deleteBlockedGeneration')
        : preview.data.reasons.some((reason) => reason === 'team_styles' || reason === 'provider_configs')
          ? t('settingsModal.teams.deleteBlockedResources')
        : preview.data.trashedProjects > 0 && preview.data.liveProjects === 0
          ? t('settingsModal.teams.deleteBlockedTrashedOnly', { count: preview.data.trashedProjects })
          : t('settingsModal.teams.deleteBlockedProjects', {
              live: preview.data.liveProjects, trashed: preview.data.trashedProjects,
            })
  const blockMessage = blocked ?? previewBlock

  // Trimmed, case-sensitive. A team name is user-chosen text the user is
  // looking at; accepting a near-match defeats the point of typing it.
  const nameMatches = typedName.trim() === team.name
  const canSubmit = nameMatches && !deleteTeam.isPending && preview.isSuccess &&
    preview.data.canDelete && !preview.isFetching && blockMessage === null

  const handleDelete = () => {
    setError(null)
    setBlocked(null)
    const trackDelete = (ok: boolean) => trackInteraction('teams_team_deleted', {
      team_id: team.id, member_count_bucket: countBucket(team.memberCount), result: resultOf(ok),
    })
    deleteTeam.mutate(team.id, {
      onSuccess: () => { trackDelete(true); onDone() },
      onError: (err) => {
        trackDelete(false)
        const counts = teamNotEmptyCounts(err)
        if (counts !== null) {
          setBlocked(
            counts.trashed > 0 && counts.live === 0
              ? t('settingsModal.teams.deleteBlockedTrashedOnly', { count: counts.trashed })
              : t('settingsModal.teams.deleteBlockedProjects', {
                  live: counts.live,
                  trashed: counts.trashed,
                }),
          )
          return
        }
        if (!(err instanceof TeamsRequestError)) {
          setError(t('settingsModal.teams.deleteFailed'))
          return
        }
        if (err.code === 'personal_team_immutable') {
          setBlocked(t('settingsModal.teams.deleteBlockedPersonal'))
          return
        }
        if (err.code === 'team_deletion_blocked') {
          const reasons = err.detail?.reasons
          setBlocked(!Array.isArray(reasons) || reasons.includes('credit_history') || reasons.includes('checkout_orders')
            ? t('settingsModal.teams.deleteBlockedHistory')
            : reasons.includes('generation_jobs')
              ? t('settingsModal.teams.deleteBlockedGeneration')
              : t('settingsModal.teams.deleteBlockedResources'))
          return
        }
        if (err.code === 'team_forbidden') {
          setBlocked(t('settingsModal.teams.deleteBlockedNotOwner'))
          return
        }
        setError(err.message_ ?? t('settingsModal.teams.deleteFailed'))
      },
    })
  }

  return (
    <div className="flex flex-col gap-4" data-testid="teams-delete-view">
      <BackButton
        testId="teams-delete-back"
        label={t('settingsModal.teams.back')}
        onClick={onCancel}
      />

      <h4 className="text-lg font-semibold text-foreground">
        {t('settingsModal.teams.deleteTitle', { name: team.name })}
      </h4>

      <WarningPanel testId="teams-delete-warning">
        <p>{t('settingsModal.teams.deleteWarning')}</p>
        <p className="mt-1.5">{t('settingsModal.teams.deleteIrreversible')}</p>
      </WarningPanel>

      {preview.isFetching && <p className="text-sm text-muted-foreground">{t('settingsModal.teams.deletePreviewLoading')}</p>}
      {preview.isError && <ErrorText testId="teams-delete-preview-error">{t('settingsModal.teams.deletePreviewFailed')}</ErrorText>}

      {blockMessage !== null && (
        <WarningPanel testId="teams-delete-blocked">{blockMessage}</WarningPanel>
      )}

      {blockMessage === null && <label className="flex flex-col gap-1.5 text-sm text-foreground">
        {t('settingsModal.teams.deleteTypeName', { name: team.name })}
        <input
          type="text"
          data-testid="teams-delete-name-input"
          value={typedName}
          onChange={(event) => setTypedName(event.target.value)}
          autoComplete="off"
          className={cn(
            'h-9 rounded-lg border border-input-border bg-input-bg px-3 text-sm text-foreground',
            'placeholder:text-text-muted',
            'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
          )}
        />
      </label>}

      <div className="flex justify-end gap-2">
        <SecondaryButton testId="teams-delete-cancel" onClick={onCancel}>
          {t('settingsModal.teams.cancel')}
        </SecondaryButton>
        <DestructiveButton
          testId="teams-delete-submit"
          disabled={!canSubmit}
          onClick={handleDelete}
        >
          {t('settingsModal.teams.deleteTeam')}
        </DestructiveButton>
      </div>

      {error !== null && <ErrorText testId="teams-delete-error">{error}</ErrorText>}
    </div>
  )
}
